import { useContext, useEffect } from 'react';
import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DataLoadingError from "@components/common/DataLoadingError";
import useSessionDetailsQuery from "@components/common/hooks/api/useSessionDetailsQuery";
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from "@lib/contexts/pageContext";
import { AbilityContext, Subjects } from "@lib/permissions";
import ParticipantForm from "@components/Pages/Events/Sessions/Participants/ParticipantForm";
import useParticipantDetailsQuery from "@components/common/hooks/api/useParticipantDetailsQuery";
import { getParticipantName } from "@components/Pages/Events/Sessions/sessionFunctions";

const EditParticipant = () => {
  const ability = useAbility(AbilityContext);
  const {
    setBreadcrumbValue,
    setPageErrorMessage,
  } = useContext(PageContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { eventID, sessionID, participantID } = useParams();
  const { data: session, ...sessionQuery } = useSessionDetailsQuery(sessionID);
  const {data: participant, ...participantQuery} = useParticipantDetailsQuery(participantID);

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', session.event);
      setBreadcrumbValue(':session', session);
    }
  }, [session, setBreadcrumbValue]);

  const updateParticipantMutation = useMutation({
    mutationFn: async (data) => {
      // Note: This only updates the participant name, not their attendance.
      const updatedParticipant = await axiosRequest({
        method: 'PUT',
        url: `${API_URLS.PARTICIPANTS}/${participantID}`,
        data: {
          ...data,
        }
      });
      return updatedParticipant;
    },
    onSuccess: async (participant) => {
      setPageErrorMessage('');
      await queryClient.invalidateQueries({ queryKey: ['participants', eventID] });
      navigate(`/events/${eventID}/${sessionID}/participants`, {
        state: { message: `${getParticipantName(participant)} has been updated.` }
      });
    },
    onError: async (err) => {
      setPageErrorMessage(['Error saving participant', err]);
      console.log('[FinalizeParticipants][save] error:', err);
    },
  });

  const onSubmit = async (data) => {
    updateParticipantMutation.mutate(data);
  }

  if (sessionQuery.isLoading || participantQuery.isLoading) {
    return (
      <Grid xs={12} textAlign="center">
        <CircularProgress/>
      </Grid>
    );
  }

  if (sessionQuery.isError && sessionQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={sessionQuery.error.errors}/>;
  }

  if (!session) {
    return (
      <>
        You do not have access, or the session was not found.
      </>
    );
  }

  if (ability.cannot('edit', subject(Subjects.SESSION, session))) {
    return <>You do not have permission to edit this session.</>;
  }

  if (participantQuery.isError && participantQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load participant!'} errors={participantQuery.error.errors}/>;
  }

  if (!participant) {
    return (
      <>
        You do not have access, or the participant was not found.
      </>
    );
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <ParticipantForm
          participant={participant}
          onSubmit={onSubmit}
        />
      </Grid>
    </Grid>
  );
};

export default EditParticipant;
